import axios from 'axios';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { API_ENDPOINTS, Icon, Icons, route } from 'carrier-fe';
import { useCallback, useEffect, useState } from 'react';

interface CarouselSlideImage {
	url: string | null;
	name: string | null;
	type: string | null;
}

interface CarouselSlide {
	id: string;
	order: number;
	image: CarouselSlideImage | null;
	url: string | null;
}

const AUTOPLAY_DELAY_MS = 5000;

function isVideoType(type?: string | null): boolean {
	return !!type && type.startsWith('video/');
}

function PromoCarousel() {
	const [slides, setSlides] = useState<CarouselSlide[]>([]);
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

	const [emblaRef, emblaApi] = useEmblaCarousel(
		{ loop: true, align: 'start' },
		[Autoplay({ delay: AUTOPLAY_DELAY_MS, stopOnInteraction: false })]
	);

	useEffect(() => {
		let cancelled = false;
		const load = async () => {
			try {
				const { data } = await axios.get<{ data: CarouselSlide[] }>(
					route(API_ENDPOINTS.DASHBOARD.CAROUSEL_SLIDES)
				);
				if (!cancelled) {
					const rows = (data?.data ?? []).filter(
						(s) => s.image?.url
					);
					setSlides(rows);
				}
			} catch {
				if (!cancelled) setSlides([]);
			}
		};
		load();
		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		if (!emblaApi) return;
		const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
		setScrollSnaps(emblaApi.scrollSnapList());
		emblaApi.on('select', onSelect);
		emblaApi.on('reInit', onSelect);
		onSelect();
		return () => {
			emblaApi.off('select', onSelect);
			emblaApi.off('reInit', onSelect);
		};
	}, [emblaApi, slides.length]);

	const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
	const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
	const scrollTo = useCallback(
		(index: number) => emblaApi?.scrollTo(index),
		[emblaApi]
	);

	if (slides.length === 0) return null;

	const showControls = slides.length > 1;

	return (
		<div className="position-relative mb-5 promo-carousel">
			<div
				className="overflow-hidden rounded-3"
				ref={emblaRef}
			>
				<div className="d-flex">
					{slides.map((slide) => {
						const src = slide.image!.url ?? '';
						const content = isVideoType(slide.image!.type) ? (
							<video
								src={src}
								autoPlay
								muted
								loop
								playsInline
								className="w-100 d-block"
								style={{ objectFit: 'cover' }}
							/>
						) : (
							<img
								src={src}
								alt=""
								className="w-100 d-block"
								style={{ objectFit: 'cover' }}
							/>
						);
						return (
							<div
								key={slide.id}
								className="flex-shrink-0"
								style={{ flex: '0 0 100%' }}
							>
								{slide.url ? (
									<a
										href={slide.url}
										target="_blank"
										rel="noopener noreferrer"
									>
										{content}
									</a>
								) : (
									content
								)}
							</div>
						);
					})}
				</div>
			</div>

			{showControls && (
				<>
					<button
						type="button"
						onClick={scrollPrev}
						aria-label="Previous slide"
						className="promo-carousel__arrow promo-carousel__arrow--prev"
						style={arrowStyle('left')}
					>
						<Icon
							icon={Icons.CHEVRON_LEFT}
							className="text-white"
						/>
					</button>
					<button
						type="button"
						onClick={scrollNext}
						aria-label="Next slide"
						className="promo-carousel__arrow promo-carousel__arrow--next"
						style={arrowStyle('right')}
					>
						<Icon
							icon={Icons.CHEVRON_RIGHT}
							className="text-white"
						/>
					</button>
					<div
						className="position-absolute d-flex gap-2"
						style={{
							bottom: 12,
							left: '50%',
							transform: 'translateX(-50%)',
							zIndex: 2,
						}}
					>
						{scrollSnaps.map((_, index) => (
							<button
								key={index}
								type="button"
								onClick={() => scrollTo(index)}
								aria-label={`Go to slide ${index + 1}`}
								style={{
									width: 10,
									height: 10,
									borderRadius: '50%',
									border: 'none',
									background:
										index === selectedIndex
											? '#fff'
											: 'rgba(255,255,255,0.5)',
									cursor: 'pointer',
								}}
							/>
						))}
					</div>
				</>
			)}
		</div>
	);
}

function arrowStyle(side: 'left' | 'right'): React.CSSProperties {
	return {
		position: 'absolute',
		top: '50%',
		[side]: 12,
		transform: 'translateY(-50%)',
		zIndex: 2,
		width: 36,
		height: 36,
		borderRadius: '50%',
		border: 'none',
		background: 'rgba(0, 0, 0, 0.5)',
		color: '#fff',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		cursor: 'pointer',
	};
}

export default PromoCarousel;
